//Imports
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
  TabContainer,
  InputNumeric
} from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import { ToggleSwitch } from "Components"; 

//Slice
import {
  manageCreateEditTemperature,
  manageTemperatureGetAvailableUnits,
  manageTemperatureGetContacts,
  reset
} from "./redux/slice";

//Styles
import "./style.scss";
import { Alert } from "@mui/material";
import { validateJsonObject } from "../../../../../../core/data/Helpers";
import { getTrimmedNonEmptyTextRules } from "Modules/.common/utils/gtcFormItemRules";

const INIT_DATA = {
  unit: null,
  zone1: {
    title: "TEST"
  }
};

const CreateEditManageTemperature = ({ refresh }) => {
  
  //States
  const [formData, setFormData] = useState(INIT_DATA);
  const [currentTab, setCurrentTab] = useState(0);
  const { messages } = useIntl();
  const [errorMessage, setErrorMessage] = useState("");

  //Refs
  const createEditTemperatureFormRef = Form.useForm();
  const zoneTabsRef = useRef(null);
  const formItemRef = useRef(null);

  //Redux
  const {
    unit,
    loading,
    error,
    updateManageTemperatureResult,
    open,
    contacts,
    loadingContacts,
    units,
    loadingUnits
  } = useSelector((state) => state.manageCreateEditManageTemperatureRedux);
  const { temperatures } = useSelector((state) => state.manageTemperatureTabRedux);

  //Memos
  const title = useMemo(
    () =>
      unit?.label
        ? `${messages["manage_createEditTemperature_EditTitle"]} "${unit?.label}"`
        : messages["manage_createEditTemperature_CreateTitle"],
    [unit]
  );
  const unitsFiltered = useMemo(() => {
    const temperaturesIds = temperatures.map(function (obj) {
      return obj.id;
    });
    return units.filter((unit) => !temperaturesIds.includes(unit.id));
  }, [temperatures, units]);

  const dispatch = useDispatch();

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      unit?.id ? messages["manage_createEditTemperature_success_edit"] : messages["manage_createEditTemperature_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, []);

  const isDisabledField = (zone, field) => {
    if (zone === 1) return false;
    const isDisabled = field ? false : true;
    return isDisabled;
  };

  //Constants
  const delays = [
    {
      id: 0,
      name: `${messages["manage_createEditTemperature_inmediate"]}`
    },
    {
      id: 300,
      name: "5 Min"
    },
    {
      id: 600,
      name: "10 Min"
    },
    {
      id: 900,
      name: "15 Min"
    },
    {
      id: 1800,
      name: "30 Min"
    },
    {
      id: 3600,
      name: `1 ${messages["manage_createEditTemperature_hour"]}`
    }
  ];

  const recurring = [
    {
      id: 0,
      name: `${messages["manage_createEditTemperature_once"]}`
    },
    {
      id: 1,
      name: `${messages["manage_createEditTemperature_recurring"]}`
    }
  ];

  const overrideTab = (newTab) => {
    setCurrentTab(newTab);
    zoneTabsRef?.current?.setValue(newTab);
  };

  const handleUpdateManageTemperatureResult = () => {
    if (!formData?.unit?.id || !formData?.zone1) {
      setErrorMessage(messages["settingsTemperature_complete_fields"]);
      return false;
    }

    let zones = [];
    let alertsToDelete = [];
    for (const property in formData) {
      if (
        property.includes(`zone`) &&
        (formData[property].enable || !formData[property].hasOwnProperty("enable"))
      ) {
        if (
          formData[property].delay &&
          String(formData[property].title ?? "").trim() &&
          formData[property].high != null &&
          formData[property].high != undefined &&
          formData[property].low != null &&
          formData[property].low != undefined &&
          formData[property].recurring?.name
        ) {
          if (parseInt(formData[property].high) <= parseInt(formData[property].low)) {
            setErrorMessage(
              `${messages["settingsTemperature_less_high_low"]} ${formData[property].zone}`
            );
            overrideTab(formData[property].zone - 1);
            return false;
          }

          if (
            parseInt(formData[property].high) < -40 ||
            parseInt(formData[property].high) > 185 ||
            parseInt(formData[property].low) < -40 ||
            parseInt(formData[property].low) > 185
          ) {
            setErrorMessage(messages["settingsTemperature_validate_high_low"]);
            overrideTab(formData[property].zone - 1);
            return false;
          }

          let contactsEdit = [];
          if (formData[property].contactIds && formData[property].contactIds.length > 0) {
            contactsEdit = formData[property].contactIds.map(function (obj) {
              return obj?.id;
            });
          } else {
            setErrorMessage(
              `${messages["settingsTemperature_subscribersRequired"]} ${formData[property].zone}`
            );
            overrideTab(formData[property].zone - 1);
            return false;
          }

          let name = String(formData[property]?.title ?? "")
            .trim()
            .replaceAll(` {${formData?.unit?.hid}}`, "");
          name = name.replaceAll(` {${formData[property].zone}}`, "").trim();

          zones.push({
            id: formData[property].id || null,
            assignedTo: [
              {
                id: null,
                entity: [
                  {
                    id: formData.unit?.id,
                    label: "Unit",
                    name: "Unit"
                  }
                ]
              }
            ],
            config: JSON.stringify({
              high: parseInt(formData[property].high),
              low: formData[property].low,
              zone: formData[property].zone,
              delay: formData[property].delay?.id,
              triggerDuration: {
                value: formData[property].delay?.id ? formData[property].delay?.id / 60 : 0,
                duration: formData[property].delay?.id ? "Minutes" : "Immediate"
              },
              recurring: {
                value: formData[property].recurring?.id === 0 ? 0 : 15,
                frequency: formData[property].recurring?.id === 0 ? "OnlyOnce" : "Minutes"
              }
            }),
            name: `${name} {${formData?.unit?.hid}} {${formData[property].zone}}`,
            severity: "Medium",
            subscribers: contactsEdit.map((contactId) => {
              return {
                alert: false,
                alertAfter: false,
                alertBefore: false,
                contactId: contactId,
                id: null
              };
            }),
            triggers: [],
            type: "Temperature"
          });
        } else {
          setErrorMessage(
            messages["settingsTemperature_complete_field_zone"] + formData[property].zone
          );
          overrideTab(formData[property].zone - 1);

          return false;
        }
      } else if (property.includes(`zone`) && !formData[property].enable && formData[property].id) {
        alertsToDelete.push({ id: formData[property]?.id });
      }
    }
    dispatch(manageCreateEditTemperature({ temperatures: zones, alertsToDelete }));
  };

  const handleSubmitErrors = (invalidValues) => {
    const keys = Object.keys(invalidValues);
    if (!keys?.length) {
      setErrorMessage("");
      return;
    }

    const lastKey = keys[keys.length - 1];
    const newTab = parseInt(lastKey[0]) - 1;
    if (formData?.unit?.id) {
      setErrorMessage(messages["manage_createEditTemperature_error_fillAllEnabledZones"]);

      overrideTab(newTab);
    }
  };

  //Effects
  useEffect(() => {
    if (!unit?.id || formData.unit || loadingContacts || loadingUnits || contacts?.length === 0) return;
    const defaultFormData = {};
    const defaultUnitValue = units.find((unitFiltered) => unitFiltered.id === unit.id);
    defaultFormData.unit = defaultUnitValue;
    unit?.temperatureAlerts?.forEach((temperatureAlert) => {
      const config = validateJsonObject(temperatureAlert?.configJSON);
      const zone = config?.zone;
      defaultFormData[`zone${zone}`] = {};
      defaultFormData[`zone${zone}`].zone = zone;
      defaultFormData[`zone${zone}`].id = temperatureAlert?.id;
      defaultFormData[`zone${zone}`].high = config?.high;
      defaultFormData[`zone${zone}`].low = config?.low;
      defaultFormData[`zone${zone}`].high = config?.high;
      defaultFormData[`zone${zone}`].delay = delays.find(
        (delay) => delay.id === config?.delay
      );
      const recurringValue = config?.recurring?.value;
      defaultFormData[`zone${zone}`].recurring = recurringValue === 0 ? recurring[0] : recurring[1];
      defaultFormData[`zone${zone}`].title = temperatureAlert?.name
        .replaceAll(` {${unit?.hid}}`, "")
        .replaceAll(` {${zone}}`, "");
      defaultFormData[`zone${zone}`].contactIds = temperatureAlert?.subscribers?.map(
        (subscriber) => {
          const contactData = contacts?.find((contact) => subscriber.contactId === contact.id);
          return contactData;
        }
      );
      defaultFormData[`${zone}inputTitle`] = defaultFormData[`zone${zone}`].title;
      defaultFormData[`${zone}inputHigh`] = defaultFormData[`zone${zone}`]?.high.toString() ?? null;
      defaultFormData[`${zone}inputLow`] = defaultFormData[`zone${zone}`]?.low.toString() ?? null;
      defaultFormData[`${zone}inputTrigger`] = defaultFormData[`zone${zone}`].delay;
      defaultFormData[`${zone}inputAlert`] = defaultFormData[`zone${zone}`].recurring;
      defaultFormData[`${zone}inputTemperatureContacts`] =
        defaultFormData[`zone${zone}`].contactIds;
      if (zone > 1) defaultFormData[`zone${zone}`].enable = true;
    });
    createEditTemperatureFormRef?.ref?.current?.setValues(defaultFormData);
    setFormData(defaultFormData);
  }, [unit, units, contacts, loadingContacts, loadingUnits]);

  useEffect(() => {
    dispatch(manageTemperatureGetContacts());
    dispatch(manageTemperatureGetAvailableUnits());
  }, []);

  useEffect(() => {
    if (!updateManageTemperatureResult) return;
    handleSuccess();
  }, [updateManageTemperatureResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    NotificationManager.error(errorMessage, messages["error"], 4000, null, null, "error");
    dispatch(reset({ error: null }));
  }, [error]);

  return (
    <Modal
      className="temperatureAddModal"
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading || loadingUnits}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditTemperatureFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        {errorMessage && (
          <div
            style={{
              paddingTop: 22,
              paddingBottom: 5
            }}
          >
            <Alert
              severity="error"
              onClose={() => {
                setErrorMessage("");
              }}
            >
              {errorMessage}
            </Alert>
          </div>
        )}
        <Form
          ref={createEditTemperatureFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageTemperatureResult()}
          onChangeValues={() => {}}
          onSubmit={(_, invalidValues) => handleSubmitErrors(invalidValues)}
        >
          <Form.Item
            label={messages["manage_createEditTemperature_UnitInputLabel"]}
            name="unit"
            rules={[{ required: true, message: messages["required_field"] }]}
          >
            <Select
              id="unit"
              placeholder={messages["manage_createEditTemperature_UnitInputLabel"]}
              isLoading={loadingUnits}
              options={unitsFiltered}
              textField="label"
              valueField="id"
              value={formData.unit?.id ? formData.unit : null}
              onChange={(value) => setFormData({ ...formData, unit: value })}
              isClearable
              isSearchable
              isDisabled={unit?.id}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Tabs ref={zoneTabsRef} tab={currentTab} onChange={(newTab) => setCurrentTab(newTab)}>
            {[1, 2, 3].map((zone, index) => (
              <TabContainer
                key={zone}
                label={`${messages["manage_temperatures_zone_label"]} ${zone}`}
                index={index}
              >
                <div>
                  {zone !== 1 && (
                    <div>
                      <ToggleSwitch
                        id={`enable${zone}`}
                        medium
                        optionLabels={[
                          messages["manage_createEditTemperature_Enable"],
                          messages["manage_createEditTemperature_Disable"]
                        ]}
                        blueToggle
                        checked={Boolean(formData?.[`zone${zone}`]?.enable)}
                        onChange={(checked) => {
                          const newZoneData = {
                            ...formData?.[`zone${zone}`],
                            zone,
                            enable: checked
                          };
                          setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                        }}
                      />
                    </div>
                  )}
                  <Form.Item
                    ref={formItemRef.current}
                    name={`${zone}inputTitle`}
                    label={messages["manage_createEditTemperature_NameInputLabel"]}
                    rules={getTrimmedNonEmptyTextRules(
                      messages["required_field"],
                      Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1
                    )}
                  >
                    <Input
                      name={`${zone}inputTitle`}
                      type="text"
                      placeholder={messages["manage_createEditTemperature_NameInputLabel"]}
                      value={formData?.[`zone${zone}`]?.title}
                      onChange={(value) => {
                        const newZoneData = { ...formData?.[`zone${zone}`], zone, title: value };
                        setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                      }}
                      required={Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1}
                      isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                      minLength={1}
                      maxLength={25}
                      minLengthMessage={`${messages["manage_createEditTemperature_minLengthMessage"]} 1`}
                      maxLengthMessage={`${messages["manage_createEditTemperature_maxLengthMessage"]} 25`}
                    />
                  </Form.Item>
                  <Form.Item
                    name={`${zone}inputHigh`}
                    label={messages["manage_createEditTemperature_HighInputLabel"]}
                    rules={[
                      {
                        required: Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1,
                        message: messages["required_field"]
                      },
                      {
                        max: 185,
                        message: `${messages["manage_createEditTemperature_maxTempMessage"]} 185`
                      },
                      {
                        min: -32,
                        message: `${messages["manage_createEditTemperature_minTempMessage"]} -32`
                      }
                    ]}
                  >
                    <InputNumeric
                      step={1}
                      name={`${zone}inputHigh`}
                      placeholder={messages["manage_createEditTemperature_HighInputLabel"]}
                      value={formData?.[`zone${zone}`]?.high != null ? formData?.[`zone${zone}`]?.high.toString() : ""}
                      onChange={(high) => {
                        const newZoneData = {
                          ...formData?.[`zone${zone}`],
                          zone,
                          high: parseInt(high)
                        };
                        setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                      }}
                      required={Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1}
                      isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                      min={-32}
                      max={185}
                    />
                  </Form.Item>
                  <Form.Item
                    name={`${zone}inputLow`}
                    label={messages["manage_createEditTemperature_LowInputLabel"]}
                    rules={[
                      {
                        required: Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1,
                        message: messages["required_field"]
                      },
                      {
                        max: 185,
                        message: `${messages["manage_createEditTemperature_maxTempMessage"]} 185`
                      },
                      {
                        min: -32,
                        message: `${messages["manage_createEditTemperature_minTempMessage"]} -32`
                      }
                    ]}
                  >
                    <InputNumeric
                      step={1}
                      name={`${zone}inputLow`}
                      placeholder={messages["manage_createEditTemperature_LowInputLabel"]}
                      value={formData?.[`zone${zone}`]?.low != null ? formData?.[`zone${zone}`]?.low.toString() : ""}
                      onChange={(low) => {
                        const newZoneData = {
                          ...formData?.[`zone${zone}`],
                          zone,
                          low: parseInt(low)
                        };
                        setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                      }}
                      required={Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1}
                      isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                      min={-32}
                      max={185}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages["manage_createEditTemperature_TriggerInputLabel"]}
                    name={`${zone}inputTrigger`}
                    rules={[
                      {
                        required: Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1,
                        message: messages["required_field"]
                      }
                    ]}
                  >
                    <Select
                      id={`${zone}inputTrigger`}
                      placeholder={messages["manage_createEditTemperature_TriggerInputLabel"]}
                      options={delays}
                      textField="name"
                      valueField="id"
                      value={formData?.[`zone${zone}`]?.delay || []}
                      onChange={(value) => {
                        const newZoneData = { ...formData?.[`zone${zone}`], zone, delay: value };
                        setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                      }}
                      isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                      isClearable
                      isSearchable
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages["manage_createEditTemperature_AlertInputLabel"]}
                    name={`${zone}inputAlert`}
                    rules={[
                      {
                        required: Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1,
                        message: messages["required_field"]
                      }
                    ]}
                  >
                    <Select
                      id={`${zone}inputAlert`}
                      name={`${zone}inputAlert`}
                      placeholder={messages["manage_createEditTemperature_AlertInputLabel"]}
                      options={recurring}
                      textField="name"
                      valueField="id"
                      value={formData?.[`zone${zone}`]?.recurring || []}
                      onChange={(value) => {
                        const newZoneData = {
                          ...formData?.[`zone${zone}`],
                          zone,
                          recurring: value
                        };
                        setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                      }}
                      isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                      isClearable
                      isSearchable
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages["manage_createEditTemperature_ContactsInputLabel"]}
                    name={`${zone}inputTemperatureContacts`}
                    rules={[
                      {
                        required: Boolean(formData?.[`zone${zone}`]?.enable) || zone === 1,
                        message: messages["required_field"]
                      }
                    ]}
                  >
                    <div style={{ width: "100%" }}>
                      <Select
                        id={`${zone}inputTemperatureContacts`}
                        name={`${zone}inputTemperatureContacts`}
                        value={formData?.[`zone${zone}`]?.contactIds || []}
                        onChange={(value) => {
                          const newZoneData = {
                            ...formData?.[`zone${zone}`],
                            zone,
                            contactIds: value
                          };
                          createEditTemperatureFormRef.ref.current.setValues({
                            [`${zone}inputTemperatureContacts`]: value,
                            [`zone${zone}`]: newZoneData
                          });
                          setFormData({ ...formData, [`zone${zone}`]: newZoneData });
                        }}
                        options={contacts || []}
                        textField="title"
                        valueField="id"
                        placeholder={messages["manage_createEditTemperature_ContactsInputLabel"]}
                        isDisabled={isDisabledField(zone, formData?.[`zone${zone}`]?.enable)}
                        style={{ width: "100%" }}
                        isClearable
                        isMulti
                        menuPlacement="top"
                        isLoading={loadingContacts}
                        showPillsOutside={false}
                        hideSelectedOptions
                        isSearchable
                      />
                    </div>
                  </Form.Item>
                </div>
              </TabContainer>
            ))}
          </Tabs>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageTemperature;
